import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Gauge, CalendarClock, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";

const ICONS = {
  invoice_overdue: AlertTriangle,
  acsa_cap_fill: Gauge,
  acsa_coverage_gap: ShieldAlert,
  billing_cycle_due: CalendarClock,
};

const SEVERITY = {
  info: { row: "border-send/40 border-l-send bg-send/20", icon: "bg-send/30 text-send" },
  amber: { row: "border-status-partial/40 border-l-status-partial bg-status-partial/20", icon: "bg-status-partial/30 text-status-partial" },
  red: { row: "border-status-void/40 border-l-status-void bg-status-void/20", icon: "bg-status-void/30 text-status-void" },
};

/** Compact, auto-clearing alert strip for the CURRENT matter, pinned at the top
 *  of the workbench. Derived live from billing state — handle the condition and
 *  the alert disappears on the next refresh. */
export function WorkbenchAlertStrip({ matterId, version, onAction }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    let active = true;
    api
      .get(`/matters/${matterId}/notifications`)
      .then((r) => active && setItems(r.data.notifications || []))
      .catch(() => active && setItems([]));
    return () => {
      active = false;
    };
  }, [matterId, version]);

  if (!items.length) return null;

  const go = (to) => {
    if (onAction) onAction(to);
    navigate(to);
  };

  return (
    <div className="space-y-2" data-testid="workbench-alert-strip">
      {items.map((n) => {
        const Icon = ICONS[n.type] || AlertTriangle;
        const sev = SEVERITY[n.severity] || SEVERITY.info;
        return (
          <div
            key={n.id}
            data-testid={`workbench-alert-${n.type}`}
            data-severity={n.severity}
            className={`flex flex-wrap items-center gap-3 rounded-lg border border-l-4 ${sev.row} px-4 py-2.5 shadow-card`}
          >
            <div className={`grid h-7 w-7 shrink-0 place-items-center rounded-md ${sev.icon}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-sm font-semibold text-foreground">{n.title}</span>
              <span className="ml-2 text-sm text-foreground/80">{n.message}</span>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {n.actions.map((a) => (
                <Button
                  key={a.testid}
                  size="sm"
                  variant={a.variant || "secondary"}
                  data-testid={`wb-${a.testid}`}
                  onClick={() => go(a.to)}
                >
                  {a.label}
                </Button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
